import { readerLocale } from '../../i18n/language';

export interface AlertView {
  kind: string;
  state: 'open' | 'used' | 'expired';
  created_at: string;
  subject_last4: string | null;
  /** "Chrome on Windows" for an unfamiliar-sign-in alert; the e-mail already said it. */
  device_label?: string | null;
}

/** The two links of the unfamiliar-sign-in e-mail (0137): "Yes, it was me" and "No, secure my account". */
export function isNewSignIn(alert: AlertView): boolean {
  return alert.kind === 'unfamiliar_sign_in_confirm' || alert.kind === 'unfamiliar_sign_in_not_me';
}

/** What the page says the alert is about — never more than the email already said. */
export function describeAlert(alert: AlertView): string {
  const when = new Date(alert.created_at).toLocaleString(readerLocale(), {
    dateStyle: 'long',
    timeStyle: 'short',
  });
  const which = alert.subject_last4 ? ` ending in ${alert.subject_last4}` : '';
  if (isNewSignIn(alert)) {
    return alert.device_label
      ? `Someone signed in to your account from ${alert.device_label} on ${when}.`
      : `Someone signed in to your account from a device we didn't recognise on ${when}.`;
  }
  if (alert.kind === 'bank_account_added') {
    return `A bank account${which} was added to your profile on ${when} as a destination for withdrawals.`;
  }
  if (alert.kind === 'bank_account_removed') {
    return `A bank account${which} was removed from your profile on ${when}.`;
  }
  if (alert.kind === 'phone_changed') {
    return `The phone number on your account was changed on ${when}.`;
  }
  if (alert.kind === 'password_changed') {
    return `The password of your account was changed on ${when}.`;
  }
  if (alert.kind === 'second_factor_changed') {
    return `The sign-in protection of your account was changed on ${when}.`;
  }
  if (alert.kind === 'withdrawal_requested') {
    return `A withdrawal was requested from your account on ${when}.`;
  }
  return `A change was made to your account on ${when}.`;
}

/**
 * What confirming will do — accurate per kind. For an ADDED account the report also
 * removes it; for a REMOVED one there is nothing left to remove, and nothing is
 * silently reinstated either: our team restores state with the client.
 */
export function describeProtection(alert: AlertView): string {
  if (isNewSignIn(alert)) {
    return (
      'If this was not you, confirm below. We will sign out every open session and ask for a '
      + 'new password the next time you sign in. Our team may also pause withdrawals and account '
      + 'changes until it has confirmed it is you.'
    );
  }
  const base =
    'sign out every open session and freeze withdrawals and account changes until our '
    + 'team has verified your identity with you. Contributions and your balances are not affected.';
  if (alert.kind === 'bank_account_added') {
    return `If this was not you, confirm below. We will remove the change, ${base}`;
  }
  return `If this was not you, confirm below. We will ${base}`;
}
